"use client";

import {
  Badge,
  Button,
  cn,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  Label,
  Loader,
  RHF,
  zodResolver,
} from "@devhop/ui";
import {
  CircleDollarSign,
  ImageIcon,
  Layers2,
  type LucideIcon,
  Package,
  ScanBarcode,
  ShieldCheck,
  Tag,
  Tags,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import type { Control, Resolver } from "react-hook-form";
import { z } from "zod";
import { FieldHelpHint } from "@/shared/ui/FieldHelpHint";
import {
  DEFAULT_SHOP_CURRENCY,
  isShopCurrency,
  SHOP_CURRENCIES,
  ShopCurrencySchema,
} from "@/shared/lib/currency";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import type { ShopSellableCatalogRow } from "../api/client";
import {
  useMasterCategoriesQuery,
  usePatchSellableShopProductSku,
} from "../api/queries";
import { useMerchantShops } from "../store/use-merchant-shops";
import { CascadingCategorySelect } from "./CascadingCategorySelect";

const intOrNull = (max: number) =>
  z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return null;
      const n = Number(val);
      return Number.isNaN(n) ? null : n;
    },
    z.union([z.number().int().min(0).max(max), z.null()]),
  );

const SkuEditFormSchema = z.object({
  sku: z.string().min(1, "ຕ້ອງໃສ່ SKU").max(100),
  barcode: z.string().max(100).optional(),
  categoryId: z.string().uuid("ຕ້ອງເລືອກໝວດ"),
  price: z.coerce.number().finite().nonnegative(),
  currency: ShopCurrencySchema,
  cost: z.coerce.number().finite().nonnegative().optional(),
  trackingMode: z.enum(["none", "lot", "lot_serial"]),
  warrantyDurationMonths: intOrNull(1200),
  warrantyMaxOdometerKm: intOrNull(10_000_000),
  warrantyTermsNote: z
    .string()
    .max(5000)
    .transform((s) => {
      const t = s.trim();
      return t === "" ? null : t;
    }),
});

type SkuEditFormValues = z.infer<typeof SkuEditFormSchema>;
type TrackingMode = SkuEditFormValues["trackingMode"];

const CURRENCY_OPTIONS = SHOP_CURRENCIES.map((c) => ({
  label: c,
  value: c,
}));

const TRACKING_MODE_CHOICES: {
  value: TrackingMode;
  title: string;
  description: string;
  Icon: LucideIcon;
}[] = [
  {
    value: "none",
    title: "ລວມສະຕ໋ອກ",
    description: "ບໍ່ແຍກ lot ຫຼື serial",
    Icon: Package,
  },
  {
    value: "lot",
    title: "ຕິດຕາມ lot",
    description: "ຮັບ/ຂາຍແຍກຕາມ lot ແລະວັນໝົດອາຍຸ",
    Icon: Layers2,
  },
  {
    value: "lot_serial",
    title: "Lot + Serial",
    description: "ຕິດຕາມຕໍ່ຊິ້ນ (serial ຜູ້ lot)",
    Icon: ScanBarcode,
  },
];

function TrackingModeRadioGroup({
  control,
}: {
  control: Control<SkuEditFormValues>;
}) {
  return (
    <RHF.Controller
      control={control}
      name="trackingMode"
      render={({ field, fieldState }) => (
        <div>
          <div
            aria-invalid={fieldState.invalid}
            aria-label="ໂໝດຕິດຕາມສະຕ໋ອກ"
            className="grid grid-cols-1 gap-3 sm:grid-cols-3"
            role="radiogroup"
            {...(fieldState.error
              ? { "aria-describedby": "tracking-mode-error" }
              : {})}
          >
            {TRACKING_MODE_CHOICES.map((choice, index) => {
              const Icon = choice.Icon;
              const selected = field.value === choice.value;
              return (
                <label
                  key={choice.value}
                  className={cn(
                    "relative block min-h-20 cursor-pointer rounded-xl border p-3 text-left ring-offset-background transition-colors",
                    "hover:bg-muted/40",
                    selected
                      ? "border-primary bg-primary/5 shadow-sm ring-2 ring-primary/25"
                      : "border-border/80 bg-muted/20",
                  )}
                >
                  <input
                    ref={index === 0 ? field.ref : undefined}
                    checked={selected}
                    className="sr-only"
                    name={field.name}
                    type="radio"
                    value={choice.value}
                    onBlur={field.onBlur}
                    onChange={() => field.onChange(choice.value)}
                  />
                  <span className="flex h-full flex-col justify-center gap-2">
                    <span className="flex items-center gap-2">
                      <span
                        className={cn(
                          "flex size-8 shrink-0 items-center justify-center rounded-md border bg-background text-primary",
                          selected ? "border-primary/50" : "border-border",
                        )}
                        aria-hidden
                      >
                        <Icon className="size-4" strokeWidth={2} />
                      </span>
                      <span className="font-medium text-foreground text-sm">
                        {choice.title}
                      </span>
                    </span>
                    <span className="text-muted-foreground text-xs leading-snug">
                      {choice.description}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {fieldState.error?.message ? (
            <p
              className="mt-2 text-destructive text-xs"
              id="tracking-mode-error"
              role="alert"
            >
              {fieldState.error.message}
            </p>
          ) : null}
        </div>
      )}
    />
  );
}

function FormPanel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "rounded-xl border border-border/80 bg-card p-4 shadow-sm sm:p-5",
        className,
      )}
    >
      {children}
    </section>
  );
}

function PanelHeading({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-4 flex gap-3">
      <span
        className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40 text-primary"
        aria-hidden
      >
        <Icon className="size-4" strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <h2 className="font-heading font-semibold text-foreground text-sm tracking-tight">
          {title}
        </h2>
        {description ? (
          <p className="mt-0.5 text-muted-foreground text-xs leading-relaxed">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ProductSummary({ row }: { row: ShopSellableCatalogRow }) {
  const src = row.imageUrl ? resolveImageSrc(row.imageUrl) : null;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-linear-to-br from-card via-card to-muted/25 p-4 sm:flex-row sm:items-center sm:gap-5">
      <div className="relative size-18 shrink-0 overflow-hidden rounded-xl border border-border bg-muted shadow-inner sm:size-20">
        {src ? (
          <img
            src={src}
            alt=""
            className="size-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex size-full items-center justify-center">
            <ImageIcon
              className="size-9 text-muted-foreground/60"
              aria-hidden
            />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <p className="font-heading font-semibold text-base text-foreground leading-snug">
          {row.productName}
        </p>
        <div className="flex flex-wrap gap-1.5">
          <Badge className="font-mono text-xs" variant="secondary">
            {row.shopSku}
          </Badge>
          {row.categoryName ? (
            <Badge className="font-normal text-xs" variant="outline">
              {row.categoryName}
            </Badge>
          ) : null}
          {row.oemPartNo ? (
            <Badge className="font-normal text-xs" variant="outline">
              OEM {row.oemPartNo}
            </Badge>
          ) : null}
          {row.condition ? (
            <Badge className="font-normal text-xs" variant="outline">
              {row.condition}
            </Badge>
          ) : null}
          {row.quantityOnHand != null ? (
            <Badge className="font-normal text-xs" variant="outline">
              ສະຕ໋ອກ {row.quantityOnHand}
            </Badge>
          ) : (
            <Badge className="font-normal text-xs" variant="outline">
              ບໍ່ຕິດຕາມສະຕ໋ອກ
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

export type ShopCatalogSkuEditFormProps = {
  shopId: string;
  row: ShopSellableCatalogRow;
  onCancel: () => void;
  onSaved: () => void;
};

export function ShopCatalogSkuEditForm({
  shopId,
  row,
  onCancel,
  onSaved,
}: ShopCatalogSkuEditFormProps) {
  const patch = usePatchSellableShopProductSku(shopId);
  const masterCategoriesQuery = useMasterCategoriesQuery(shopId);
  const { currentShop } = useMerchantShops(shopId);
  const shopRowCurrency =
    currentShop?.shopDefaultCurrency ?? DEFAULT_SHOP_CURRENCY;
  const skuCurrencyLocked = currentShop?.enforceSingleShopSkuCurrency ?? false;

  const form = RHF.useForm<SkuEditFormValues>({
    resolver: zodResolver(SkuEditFormSchema) as Resolver<SkuEditFormValues>,
    defaultValues: {
      sku: "",
      barcode: "",
      categoryId: "",
      price: 0,
      currency: shopRowCurrency,
      cost: 0,
      trackingMode: "none" as const,
      warrantyDurationMonths: null as number | null,
      warrantyMaxOdometerKm: null as number | null,
      warrantyTermsNote: "",
    },
  });

  useEffect(() => {
    const rowCur = isShopCurrency(row.currency)
      ? row.currency
      : DEFAULT_SHOP_CURRENCY;
    form.reset({
      sku: row.shopSku,
      barcode: row.barcode ?? "",
      categoryId: row.categoryId ?? "",
      price: row.price,
      currency: skuCurrencyLocked ? shopRowCurrency : rowCur,
      cost: row.cost != null ? row.cost : 0,
      trackingMode:
        row.trackingMode === "lot" || row.trackingMode === "lot_serial"
          ? row.trackingMode
          : row.trackingMode === "serial"
            ? "lot_serial"
            : "none",
      warrantyDurationMonths: row.warrantyDurationMonths,
      warrantyMaxOdometerKm: row.warrantyMaxOdometerKm,
      warrantyTermsNote: row.warrantyTermsNote ?? "",
    });
  }, [row, form, skuCurrencyLocked, shopRowCurrency]);

  const categoryId = form.watch("categoryId");

  const onSubmit = async (vals: SkuEditFormValues) => {
    try {
      const currencyOut = skuCurrencyLocked ? shopRowCurrency : vals.currency;
      await patch.mutateAsync({
        shopProductSkuId: row.id,
        body: {
          sku: vals.sku,
          price: vals.price,
          currency: currencyOut,
          barcode: vals.barcode?.trim() ? vals.barcode.trim() : null,
          cost: vals.cost,
          trackingMode: vals.trackingMode,
          categoryId: vals.categoryId,
          warrantyDurationMonths: vals.warrantyDurationMonths,
          warrantyMaxOdometerKm: vals.warrantyMaxOdometerKm,
          warrantyTermsNote: vals.warrantyTermsNote,
        },
      });
      onSaved();
    } catch {
      // toast ຈາກ mutation onError
    }
  };

  return (
    <FormRoot<SkuEditFormValues>
      methods={form}
      onSubmit={(v) => onSubmit(v)}
      className="space-y-5"
    >
      <ProductSummary row={row} />

      <FormPanel>
        <PanelHeading
          icon={Tags}
          title={"\u0edc\u0ea7\u0e94\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2"}
          description={
            "\u0ec0\u0ea5\u0eb7\u0ead\u0e81\u0e88\u0eb2\u0e81\u0edc\u0ea7\u0e94\u0eab\u0ebc\u0eb1\u0e81\u0ec4\u0e9b\u0eab\u0eb2\u0edc\u0ea7\u0e94\u0e8d\u0ec8\u0ead\u0e8d\u0e88\u0ebb\u0e99\u0eaa\u0eb8\u0e94"
          }
        />
        <div className="space-y-1.5">
          <Label>
            {"\u0edc\u0ea7\u0e94"} <span className="text-destructive">*</span>
          </Label>
          <CascadingCategorySelect
            categories={masterCategoriesQuery.data?.items ?? []}
            value={categoryId ?? ""}
            onChange={(val) =>
              form.setValue("categoryId", val, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            isLoading={masterCategoriesQuery.isLoading}
            clearable={false}
            requireLeaf
          />
          {form.formState.errors.categoryId ? (
            <p className="text-destructive text-xs">
              {form.formState.errors.categoryId.message}
            </p>
          ) : null}
        </div>
      </FormPanel>

      <FormPanel>
        <PanelHeading
          icon={Tag}
          title={"\u0ea5\u0eb0\u0eab\u0eb1\u0e94 \u0ec1\u0ea5\u0eb0 \u0eaa\u0eb0\u0ec1\u0e81\u0e99"}
          description={
            "SKU \u0e82\u0ead\u0e87\u0eae\u0ec9\u0eb2\u0e99 \u0ec1\u0ea5\u0eb0 barcode \u0eaa\u0eb3\u0ea5\u0eb1\u0e9a POS"
          }
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormInput
            name="sku"
            label={
              <FieldHelpHint label={"SKU \u0e82\u0ead\u0e87\u0eae\u0ec9\u0eb2\u0e99"}>
                {
                  "\u0ea5\u0eb0\u0eab\u0eb1\u0e94\u0eaa\u0eb4\u0e99\u0e84\u0ec9\u0eb2\u0e82\u0ead\u0e87\u0eae\u0ec9\u0eb2\u0e99 \u2014 \u0ec3\u0e8a\u0ec9\u0ec3\u0e99 POS / \u0eaa\u0eb0\u0ec1\u0e81\u0e99 / \u0e84\u0ebb\u0ec9\u0e99\u0eab\u0eb2. \u0ec1\u0e95\u0e81\u0e95\u0ec8\u0eb2\u0e87\u0e88\u0eb2\u0e81 OEM Part No \u0e82\u0ead\u0e87 catalog."
                }
              </FieldHelpHint>
            }
            requiredMark
            placeholder="SKU"
          />
          <FormInput
            name="barcode"
            label="Barcode"
            placeholder={"\u0ea7\u0ec8\u0eb2\u0e87\u0ec4\u0e94\u0ec9"}
          />
        </div>
      </FormPanel>

      <FormPanel>
        <PanelHeading
          icon={CircleDollarSign}
          title="ລາຄາ ແລະ ຕົ້ນທຶນ"
          description="ສະກຸນເງິນຕາມການຕັ້ງຄ່າຮ້ານ (ຖ້າບັງຄັບ)"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormPrice
            name="price"
            label="ລາຄາຂາຍ"
            currencySymbol="K"
            requiredMark
          />
          {skuCurrencyLocked ? (
            <div className="space-y-2">
              <Label>ສະກຸນເງິນ</Label>
              <p className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 font-mono text-sm">
                {shopRowCurrency}
              </p>
              <p className="text-muted-foreground text-xs leading-snug">
                ຮ້ານບັງຄັບໃຫ້ SKU ໃຊ້ສະກຸນດຽວກັບຕັ້ງຄ່າຮ້ານ (MC2)
              </p>
              <input type="hidden" {...form.register("currency")} />
            </div>
          ) : (
            <FormSelect
              name="currency"
              label="ສະກຸນເງິນ"
              options={CURRENCY_OPTIONS}
            />
          )}
        </div>
        <div className="mt-4 max-w-md">
          <FormPrice
            name="cost"
            label="ຕົ້ນທຶນ (ທາງເລືອກ)"
            currencySymbol="K"
            placeholder="ວ່າງໄດ້"
          />
        </div>
      </FormPanel>

      <FormPanel>
        <PanelHeading
          icon={Layers2}
          title="ໂໝດຕິດຕາມສະຕ໋ອກ"
          description="ເລືອກວິທີຮັບ-ຂາຍສະຕ໋ອກສຳລັບ SKU ນີ້"
        />
        <TrackingModeRadioGroup control={form.control} />
      </FormPanel>

      <div className="relative overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm">
        <div
          aria-hidden
          className="absolute inset-y-0 start-0 w-1 bg-primary"
        />
        <div className="border-border/60 border-b bg-muted/30 px-4 py-3 sm:px-5">
          <div className="flex items-start gap-3">
            <span
              className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary"
              aria-hidden
            >
              <ShieldCheck className="size-4" strokeWidth={2} />
            </span>
            <div>
              <h2 className="font-heading font-semibold text-foreground text-sm">
                ປະກັນ — ຄ່າເລີ່ມຕົ້ນ
              </h2>
              <p className="mt-0.5 text-muted-foreground text-xs leading-relaxed">
                ໃຊ້ຕອນຂາຍເພື່ອ snapshot ປະກັນ — 0 = ບໍ່ມີປະກັນ; ວ່າງ = ບໍ່ໃຊ້ຄ່າເລີ່ມຕົ້ນ
              </p>
            </div>
          </div>
        </div>
        <div className="space-y-4 p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormInput
              name="warrantyDurationMonths"
              type="number"
              inputMode="numeric"
              min={0}
              max={1200}
              step={1}
              label="ໄລຍະປະກັນ (ເດືອນ)"
              placeholder="ວ່າງໄດ້"
            />
            <FormInput
              name="warrantyMaxOdometerKm"
              type="number"
              inputMode="numeric"
              min={0}
              max={10_000_000}
              step={1}
              label="ໄລຍະທາງສູງສຸດ (ກມ)"
              placeholder="ວ່າງໄດ້"
            />
          </div>
          <FormTextarea
            name="warrantyTermsNote"
            label="ຂໍ້ຄວາມສັ້ນ (ໃບຮັບເງິນ / ລາຍລະອຽດ)"
            rows={3}
            placeholder="ວ່າງໄດ້ — ບໍ່ແທນທີ່ກົດໝາຍ"
          />
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 border-border border-t pt-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          ຍົກເລີກ
        </Button>
        <Button
          className="min-w-36 sm:min-w-40"
          disabled={patch.isPending}
          type="submit"
        >
          {patch.isPending ? <Loader /> : null}
          <span className={patch.isPending ? "ml-2" : ""}>ບັນທຶກ</span>
        </Button>
      </div>
    </FormRoot>
  );
}
